/* eslint-disable @stencil-community/own-methods-must-be-private */
/* eslint-disable @stencil-community/own-props-must-be-private */
/* STENCIL IMPORTS */
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  Method
} from "@stencil/core";

import { MercuryBundles, getIconPath } from "@genexus/mercury";
import { Locale } from "../../common/locale";
import { UploadedFile, FileUploadState, UploadResult } from "./types";

const PLUS_ICON = getIconPath({
  category: "system",
  name: "add",
  colorType: "primary"
});

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/icon",
  "components/list-box",
  "components/edit",
  "utils/typography",
  "utils/spacing",
  "utils/form",
  "utils/layout",
  "utils/elevation",
  "chameleon/scrollbar"
];

type FileValidationResult = {
  isValid: boolean;
  error?: string;
};

@Component({
  tag: "gx-ide-file-uploader",
  styleUrl: "file-uploader.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/file-uploader"]
})
export class FileUploader {
  #_componentLocale: any;
  #fileInputEl!: HTMLInputElement;

  @Element() el!: HTMLGxIdeFileUploaderElement;

  /** Allowed file types/extensions (e.g. ['.jpg', '.png', 'image/*', 'application/pdf']) */
  @Prop() readonly types?: string[] = [];

  /** Whether multiple files can be selected */
  @Prop() readonly multiple: boolean = false;

  /** Whether file names can be edited */
  @Prop() readonly canEditName: boolean = false;

  /** Label to display in the uploader dialog */
  @Prop() readonly label?: string;

  /** Function to handle file upload */
  @Prop() readonly uploadFunction!: (
    file: File,
    progressCallback: (progress: number) => void
  ) => Promise<UploadResult>;

  /** Function to cancel file upload */
  @Prop() readonly cancelUploadFunction?: (fileId: string) => Promise<void>;

  /** Callback when a file is uploaded successfully */
  @Prop() readonly fileUploadedCallback?: (file: UploadedFile) => Promise<void>;

  /** Callback when the dialog is canceled */
  @Prop() readonly dialogCanceledCallback?: () => Promise<void>;

  /** Callback when the dialog is confirmed with files */
  @Prop() readonly dialogConfirmedCallback?: (
    files: UploadedFile[]
  ) => Promise<void>;

  /** Callback when upload is cancelled */
  @Prop() readonly uploadCancelledCallback?: (fileId: string) => Promise<void>;

  /** Callback when file is renamed */
  @Prop() readonly fileRenamedCallback?: (data: {
    fileId: string;
    newName: string;
  }) => Promise<void>;

  /** Callback when a file validation error occurs */
  @Prop() readonly fileValidationErrorCallback?: (data: {
    fileName: string;
    error: string;
  }) => Promise<void>;

  /** Callback when a file deletion error occurs */
  @Prop() readonly fileDeletionErrorCallback?: (data: {
    fileName: string;
    error: string;
  }) => Promise<void>;

  /** Callback when a file is deleted */
  @Prop() readonly fileDeletedCallback?: (file: UploadedFile) => Promise<void>;

  /** Callback before deleting a file to get confirmation */
  @Prop() readonly onBeforeDelete?: (file: UploadedFile) => Promise<boolean>;

  @State() private fileStates: Map<string, FileUploadState> = new Map();
  @State() private isDragging: boolean = false;
  #dragCounter = 0;

  /** Initializes component with locale strings */
  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
  }

  /** Generates a unique ID for file identification */
  #generateId = (): string => {
    return Math.random().toString(36).substr(2, 9);
  };

  /** Validates if a file matches the allowed types */
  #isValidFile(file: File): FileValidationResult {
    // If no types specified, accept any file
    if (!this.types || this.types.length === 0) {
      return { isValid: true };
    }

    for (const type of this.types) {
      // Handle wildcards like 'image/*'
      if (type.includes("/*")) {
        const baseType = type.split("/")[0];
        if (file.type.startsWith(baseType)) {
          return { isValid: true };
        }
      }
      // Handle extensions like '.jpg'
      else if (type.startsWith(".")) {
        if (file.name.toLowerCase().endsWith(type.toLowerCase())) {
          return { isValid: true };
        }
      }
      // Handle specific MIME types
      else if (file.type === type || file.type === `application/${type}`) {
        return { isValid: true };
      }
    }

    return {
      isValid: false,
      error:
        this.#_componentLocale?.errors?.invalidFileType || "Invalid file type"
    };
  }

  /** Filters valid files from a FileList */
  #getValidFiles(files: FileList): File[] {
    const validFiles: File[] = [];

    Array.from(files).forEach(file => {
      const validation = this.#isValidFile(file);
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        this.fileValidationErrorCallback?.({
          fileName: file.name,
          error: validation.error
        });
      }
    });

    return validFiles;
  }

  /** Handles drag enter event */
  #handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.#dragCounter++;
    this.isDragging = true;
  };

  /** Handles drag leave event */
  #handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.#dragCounter--;
    if (this.#dragCounter === 0) {
      this.isDragging = false;
    }
  };

  /** Handles drag over event */
  #handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /** Handles drop event */
  #handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.isDragging = false;
    this.#dragCounter = 0;

    const files = e.dataTransfer?.files;
    if (files) {
      this.#handleFiles(files);
    }
  };

  /** Triggers file input click */
  #handleFileSelect = () => {
    this.#fileInputEl?.click();
  };

  /** Handles file input change event */
  #handleFileInput = (e: Event) => {
    const files = (e.target as HTMLInputElement).files;
    if (files) {
      this.#handleFiles(files);
    }
  };

  /** Processes files and starts upload */
  #handleFiles = (files: FileList) => {
    const validFiles: File[] = this.#getValidFiles(files);

    validFiles.forEach(async file => {
      const id = this.#generateId();
      const fileState: FileUploadState = {
        id,
        name: file.name,
        file,
        pending: true,
        progress: 0
      };

      if (!this.multiple) {
        this.fileStates.clear();
      }

      this.fileStates = new Map(this.fileStates.set(id, fileState));

      if (this.uploadFunction) {
        try {
          const uploadPromise = this.uploadFunction(file, progress => {
            const state = this.fileStates.get(id);
            if (state) {
              const updatedState = {
                ...state,
                progress: progress,
                pending: progress < 100
              };
              this.fileStates = new Map(this.fileStates.set(id, updatedState));
            }
          });

          // Store the upload promise/object for potential cancellation
          const stateWithPromise = {
            ...this.fileStates.get(id),
            uploadPromise
          };
          this.fileStates = new Map(this.fileStates.set(id, stateWithPromise));

          const result = await uploadPromise;

          const state = this.fileStates.get(id);
          if (state) {
            if (result.success && result.remoteId) {
              const updatedState = {
                ...state,
                remoteId: result.remoteId,
                pending: false,
                progress: 100
              };
              this.fileStates = new Map(this.fileStates.set(id, updatedState));

              await this.fileUploadedCallback?.({
                name: state.name,
                originalName: state.file.name,
                remoteId: result.remoteId
              });
            } else {
              const updatedState = {
                ...state,
                error: result.errors?.upload || "Upload failed",
                pending: false
              };
              this.fileStates = new Map(this.fileStates.set(id, updatedState));
            }
          }
        } catch (error) {
          const state = this.fileStates.get(id);
          if (state) {
            const updatedState = {
              ...state,
              error:
                error instanceof Error
                  ? error.message
                  : "Unknown error occurred",
              pending: false
            };
            this.fileStates = new Map(this.fileStates.set(id, updatedState));
          }
        }
      }
    });

    if (this.#fileInputEl) {
      this.#fileInputEl.value = "";
    }
  };

  /** Handles file name change */
  #handleNameChange = async (
    event: CustomEvent<{ id: string; name: string }>
  ) => {
    const { id, name } = event.detail;
    const file = this.fileStates.get(id);

    if (file) {
      const updatedFile = {
        ...file,
        name: name.trim(),
        isEditing: false
      };

      this.fileStates = new Map(this.fileStates.set(id, updatedFile));
      await this.fileRenamedCallback?.({ fileId: id, newName: name.trim() });
    }
  };

  /** Handles individual file cancellation or deletion */
  #handleFileCancel = async (event: CustomEvent<string>) => {
    const id = event.detail;
    const state = this.fileStates.get(id);
    if (state) {
      try {
        if (state.pending) {
          // Cancel upload in progress
          if (
            state.uploadPromise &&
            typeof state.uploadPromise.cancel === "function"
          ) {
            state.uploadPromise.cancel();
          } else if (this.cancelUploadFunction) {
            await this.cancelUploadFunction(id);
          }

          await this.uploadCancelledCallback?.(id);

          this.fileStates.delete(id);
          this.fileStates = new Map(this.fileStates);
        } else {
          const fileToDelete: UploadedFile = {
            name: state.name,
            originalName: state.file.name,
            remoteId: state.remoteId
          };

          const shouldDelete = await this.onBeforeDelete?.(fileToDelete);

          if (shouldDelete) {
            await this.fileDeletedCallback?.(fileToDelete);
            this.fileStates.delete(id);
            this.fileStates = new Map(this.fileStates);
          }
        }
      } catch (error) {
        await this.fileDeletionErrorCallback?.({
          fileName: state.name,
          error:
            error instanceof Error ? error.message : "Unknown error occurred"
        });
      }
    }
  };

  /** Handles dialog confirmation */
  #handleConfirm = async () => {
    // Verificar si hay subidas en progreso
    const hasPendingUploads = Array.from(this.fileStates.values()).some(
      file => file.pending
    );

    if (hasPendingUploads) {
      return; // No permitir confirmar si hay subidas en progreso
    }

    // Obtener todos los archivos que están completamente subidos
    const uploadedFiles: UploadedFile[] = Array.from(this.fileStates.values())
      .filter(file => {
        const isValid = !file.pending && file.remoteId && !file.error;
        return isValid;
      })
      .map(file => ({
        name: file.name,
        originalName: file.file.name,
        remoteId: file.remoteId as string
      }));

    if (uploadedFiles.length > 0) {
      await this.dialogConfirmedCallback?.(uploadedFiles);
    }

    // Clear file states
    this.fileStates.clear();
    this.fileStates = new Map();

    // Reset file input
    if (this.#fileInputEl) {
      this.#fileInputEl.value = "";
    }
  };

  /** Handles dialog cancellation */
  #handleCancel = async () => {
    try {
      // Cancelamos todas las subidas en progreso
      for (const [id, state] of this.fileStates.entries()) {
        if (state.pending && this.cancelUploadFunction) {
          try {
            await this.cancelUploadFunction(id);
            await this.uploadCancelledCallback?.(id);
          } catch (error) {
            // Error handling for upload cancellation
          }
        }
      }

      // Limpiamos todos los estados
      this.fileStates.clear();
      this.fileStates = new Map();

      if (this.#fileInputEl) {
        this.#fileInputEl.value = "";
      }

      await this.dialogCanceledCallback?.();
    } catch (error) {
      this.fileStates.clear();
      this.fileStates = new Map();
      await this.dialogCanceledCallback?.();
    }
  };

  /** Clears the current file selection */
  @Method()
  async clear() {
    for (const [id, state] of this.fileStates.entries()) {
      if (state.pending && this.cancelUploadFunction) {
        try {
          await this.cancelUploadFunction(id);
          await this.uploadCancelledCallback?.(id);
        } catch (error) {
          // Error handling for upload cancellation
        }
      }
    }
    this.fileStates.clear();
  }

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        {/* Native file input required: ch-edit is for text only; no Chameleon file picker. */}
        <input
          type="file"
          hidden
          multiple={this.multiple}
          accept={this.types
            ?.map(type => (type.startsWith(".") ? type : `.${type}`))
            .join(",")}
          onChange={this.#handleFileInput}
          ref={el => (this.#fileInputEl = el as HTMLInputElement)}
          aria-hidden="true"
          tabIndex={-1}
        />
        <div class="file-uploader-dialog">
          <div class="dialog-content">
            {this.isDragging || this.fileStates.size === 0 ? (
              <div
                class={{
                  "file-uploader__drop-zone elevation-1": true,
                  "file-uploader__drop-zone--dragging": this.isDragging
                }}
                onDragEnter={this.#handleDragEnter}
                onDragLeave={this.#handleDragLeave}
                onDragOver={this.#handleDragOver}
                onDrop={this.#handleDrop}
              >
                <button
                  class="button-tertiary button-icon-and-text"
                  type="button"
                  onClick={this.#handleFileSelect}
                >
                  <ch-image class="icon-m" src={PLUS_ICON}></ch-image>
                  <label class="label">
                    {this.multiple
                      ? this.#_componentLocale?.header.uploadButtonMultiple
                      : this.#_componentLocale?.header.uploadButtonSingle}
                  </label>
                </button>
                <div class="text-container">
                  <div class="label-primary-row">
                    <label class="label">
                      {this.label || this.#_componentLocale?.header.orText}
                    </label>
                    <label class="label">
                      {this.#_componentLocale?.header.dragAndDrop}
                    </label>
                  </div>
                  <span class="caption">
                    {this.types && this.types.length > 0
                      ? this.#_componentLocale?.header.acceptedFiles.replace(
                        "{0}",
                        this.types.join(", ")
                      )
                      : this.#_componentLocale?.header.allFilesAccepted}
                  </span>
                </div>
              </div>
            ) : (
              <div class="file-uploader__list">
                <div class="file-uploader__list-header elevation-1 spacing-body-block-end spacing-inline-end">
                  <div class="file-uploader__list-title-container">
                    <label class="label">
                      {this.label ||
                        this.#_componentLocale?.fileList.attachedFiles}
                    </label>
                    <span class="caption">
                      {this.#_componentLocale?.fileList.filesCount.replace(
                        "{0}",
                        this.fileStates.size.toString()
                      )}
                    </span>
                  </div>
                  {this.multiple && (
                    <button
                      class="button-tertiary button-icon-and-text"
                      type="button"
                      onClick={this.#handleFileSelect}
                    >
                      <ch-image class="icon-m" src={PLUS_ICON}></ch-image>
                      <label class="label">
                        {this.multiple
                          ? this.#_componentLocale?.header.uploadButtonMultiple
                          : this.#_componentLocale?.header.uploadButtonSingle}
                      </label>
                    </button>
                  )}
                </div>
                <ul
                  class="file-uploader__list-content scrollable spacing-inline-end spacing-block-end"
                  aria-live="polite"
                  aria-label={this.#_componentLocale?.fileList.selectedFilesAriaLabel}
                >
                  {Array.from(this.fileStates.values()).map(file => (
                    <gx-ide-file-item
                      key={file.id}
                      file={file}
                      editable={this.canEditName}
                      componentLocale={this.#_componentLocale}
                      onFileNameChange={this.#handleNameChange}
                      onFileDelete={this.#handleFileCancel}
                      onFileCancel={this.#handleFileCancel}
                    />
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div class={{
            "dialog-footer": true,
            "dialog-footer-with-border": this.fileStates.size > 0
          }}>
            <div class="buttons-spacer">
              <button
                class="button-secondary"
                type="button"
                onClick={this.#handleCancel}
              >
                {this.#_componentLocale?.buttons.cancel}
              </button>
              <button
                class="button-primary"
                type="button"
                onClick={this.#handleConfirm}
                disabled={
                  this.fileStates.size === 0 ||
                  Array.from(this.fileStates.values()).some(
                    file => file.pending
                  )
                }
              >
                {this.#_componentLocale?.buttons.confirm}
              </button>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
